import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { InclusiveExclusiveEntryDto, InclusiveExclusiveTemplateDto } from '@travel-planner/library-travel-planner-api';
import { LoadingState } from '../../../../core/models/common';
import { InclusiveExclusiveActions } from './inclusive-exclusive.actions';

export interface InclusiveExclusiveState {
    entries: EntityState<InclusiveExclusiveEntryDto>;
    templates: EntityState<InclusiveExclusiveTemplateDto>;
    entriesLoadingState: LoadingState;
    templatesLoadingState: LoadingState;
    savingState: LoadingState;
    totalEntries: number | null;
}

export const entriesAdapter: EntityAdapter<InclusiveExclusiveEntryDto> = createEntityAdapter<InclusiveExclusiveEntryDto>({
    sortComparer: (a, b) => (a.text ?? '').localeCompare(b.text ?? '')
});

export const templatesAdapter: EntityAdapter<InclusiveExclusiveTemplateDto> = createEntityAdapter<InclusiveExclusiveTemplateDto>({
    sortComparer: (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
});

const initialState: InclusiveExclusiveState = {
    entries: entriesAdapter.getInitialState(),
    templates: templatesAdapter.getInitialState(),
    entriesLoadingState: 'Initial',
    templatesLoadingState: 'Initial',
    savingState: 'Initial',
    totalEntries: null
};

export const inclusiveExclusiveFeature = createFeature({
    name: 'inclusiveExclusive',
    reducer: createReducer(
        initialState,
        on(InclusiveExclusiveActions.loadEntries, (state) => ({
            ...state,
            entriesLoadingState: 'Loading' as const
        })),
        on(InclusiveExclusiveActions.loadEntriesSuccess, (state, { entries, total }) => ({
            ...state,
            entries: entriesAdapter.setAll(entries, state.entries),
            entriesLoadingState: 'Loaded' as const,
            totalEntries: total ?? state.totalEntries
        })),
        on(
            InclusiveExclusiveActions.createEntry,
            InclusiveExclusiveActions.updateEntry,
            InclusiveExclusiveActions.deleteEntry,
            InclusiveExclusiveActions.createTemplate,
            InclusiveExclusiveActions.updateTemplate,
            InclusiveExclusiveActions.deleteTemplate,
            (state) => ({
                ...state,
                savingState: 'Loading' as const
            })
        ),
        on(InclusiveExclusiveActions.createEntrySuccess, (state, { entry }) => ({
            ...state,
            entries: entriesAdapter.setOne(entry, state.entries),
            savingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.updateEntrySuccess, (state, { entry }) => ({
            ...state,
            entries: entriesAdapter.setOne(entry, state.entries),
            savingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.deleteEntrySuccess, (state, { id }) => ({
            ...state,
            entries: entriesAdapter.removeOne(id, state.entries),
            templates: templatesAdapter.map(template => ({
                ...template,
                inclusiveItems: (template.inclusiveItems ?? []).filter(item => item.entry?.id !== id),
                exclusiveItems: (template.exclusiveItems ?? []).filter(item => item.entry?.id !== id)
            }), state.templates),
            savingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.loadTemplates, (state) => ({
            ...state,
            templatesLoadingState: 'Loading' as const
        })),
        on(InclusiveExclusiveActions.loadTemplatesSuccess, (state, { templates }) => ({
            ...state,
            templates: templatesAdapter.setAll(templates, state.templates),
            templatesLoadingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.createTemplateSuccess, (state, { template }) => ({
            ...state,
            templates: templatesAdapter.setOne(template, state.templates),
            savingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.updateTemplateSuccess, (state, { template }) => ({
            ...state,
            templates: templatesAdapter.setOne(template, state.templates),
            savingState: 'Loaded' as const
        })),
        on(InclusiveExclusiveActions.deleteTemplateSuccess, (state, { id }) => ({
            ...state,
            templates: templatesAdapter.removeOne(id, state.templates),
            savingState: 'Loaded' as const
        })),
        on(
            InclusiveExclusiveActions.loadEntriesFailure,
            InclusiveExclusiveActions.createEntryFailure,
            InclusiveExclusiveActions.updateEntryFailure,
            InclusiveExclusiveActions.deleteEntryFailure,
            InclusiveExclusiveActions.loadTemplatesFailure,
            InclusiveExclusiveActions.createTemplateFailure,
            InclusiveExclusiveActions.updateTemplateFailure,
            InclusiveExclusiveActions.deleteTemplateFailure,
            (state) => ({
                ...state,
                entriesLoadingState: 'Error' as const,
                templatesLoadingState: 'Error' as const,
                savingState: 'Error' as const
            })
        )
    )
});

const { selectInclusiveExclusiveState } = inclusiveExclusiveFeature;

const selectSafeInclusiveExclusiveState = createSelector(
    selectInclusiveExclusiveState,
    state => state ?? initialState
);

const selectEntriesState = createSelector(selectSafeInclusiveExclusiveState, state => state.entries);
const selectTemplatesState = createSelector(selectSafeInclusiveExclusiveState, state => state.templates);

const { selectAll: selectAllEntries, selectEntities: selectEntryEntities } = entriesAdapter.getSelectors(selectEntriesState);
const { selectAll: selectAllTemplates, selectEntities: selectTemplateEntities } = templatesAdapter.getSelectors(selectTemplatesState);

export const selectInclusiveExclusiveEntries = selectAllEntries;
export const selectInclusiveExclusiveTemplates = selectAllTemplates;
export const selectInclusiveExclusiveEntryEntities = selectEntryEntities;
export const selectInclusiveExclusiveTemplateEntities = selectTemplateEntities;

export const selectInclusiveExclusiveTemplate = (id: number | string) => createSelector(
    selectInclusiveExclusiveTemplateEntities,
    entities => entities[id] ?? null
);

export const selectInclusiveExclusiveEntriesLoadingState = createSelector(
    selectSafeInclusiveExclusiveState,
    state => state.entriesLoadingState
);

export const selectInclusiveExclusiveTemplatesLoadingState = createSelector(
    selectSafeInclusiveExclusiveState,
    state => state.templatesLoadingState
);

export const selectInclusiveExclusiveSavingState = createSelector(
    selectSafeInclusiveExclusiveState,
    state => state.savingState
);
